import { z } from 'zod';
import { SOURCE_PACK_CONTENT_MAX_BYTES } from '../constants.js';

// ─── Scope ────────────────────────────────────────────────────────────────────

/** 'workspace' (every product) or 'product:<slug>' (one product). */
export const SourcePackFileScopeSchema = z
  .string()
  .refine(
    (s) => s === 'workspace' || /^product:[a-z0-9_-]+$/.test(s),
    "scope must be 'workspace' or 'product:<slug>'"
  );
export type SourcePackFileScope = z.infer<typeof SourcePackFileScopeSchema>;

// ─── Request schemas ──────────────────────────────────────────────────────────

export const CreateSourcePackFileRequestSchema = z.object({
  filename: z.string().min(1).max(500),
  content: z.string().max(SOURCE_PACK_CONTENT_MAX_BYTES, 'content exceeds 1MB'),
  scope: SourcePackFileScopeSchema.optional().default('workspace'),
});
export type CreateSourcePackFileRequest = z.infer<typeof CreateSourcePackFileRequestSchema>;

export const UpdateSourcePackFileRequestSchema = z.object({
  filename: z.string().min(1).max(500).optional(),
  content: z.string().max(SOURCE_PACK_CONTENT_MAX_BYTES, 'content exceeds 1MB').optional(),
  scope: SourcePackFileScopeSchema.optional(),
});
export type UpdateSourcePackFileRequest = z.infer<typeof UpdateSourcePackFileRequestSchema>;

// ─── Response schemas ─────────────────────────────────────────────────────────

export const SourcePackFileResponseSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int().positive(),
  filename: z.string(),
  // Both content and contentHash are optional because the list endpoint
  // omits them for payload-size reasons.
  content: z.string().optional(),
  contentHash: z.string().optional(),
  scope: SourcePackFileScopeSchema,
  isCurrent: z.boolean().optional(),
  createdAt: z.string(),
  createdBy: z.string().uuid().nullable().optional(),
});
export type SourcePackFileResponse = z.infer<typeof SourcePackFileResponseSchema>;

export const ListSourcePackFilesResponseSchema = z.object({
  ok: z.literal(true),
  items: z.array(SourcePackFileResponseSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});
export type ListSourcePackFilesResponse = z.infer<typeof ListSourcePackFilesResponseSchema>;
